import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { DesignScopeListItem } from "mock-data/design-scope";
import { getFeatureEntityLog } from "services/feature-apis";
import { ProjectContext } from "context/ProjectProvider";
import type { TAuditChangeLog } from "change-events/change-event-polling";
import { FeatureTypes } from "entity-app/constants";
import { MAX_NUMBER_OF_FETCH_ITEM } from "constants/index";

/** report-engine feature type for Design Scope (`GET .../logs/10`). */
export const DESIGN_SCOPE_FEATURE_TYPE = FeatureTypes.DESIGN_SCOPE;

const DESIGN_SCOPE_FEATURE_TYPE_LABEL = "DESIGN_SCOPE";
const DELETE_CHANGE_TYPES = new Set(["delete", "bulk_delete"]);

function isDesignScopeFeatureLog(log: TAuditChangeLog): boolean {
  return (
    log.data_source === "feature_instance" &&
    (log.info?.feature_type === DESIGN_SCOPE_FEATURE_TYPE_LABEL ||
      !!log.info?.feature_type_ids?.includes(FeatureTypes.DESIGN_SCOPE))
  );
}

function flattenFeatureInstanceIds(logs: TAuditChangeLog[]): string[] {
  const nested = logs.map((log) => log.info?.feature_instance_ids ?? []);
  return [...new Set(([] as string[]).concat(...nested))];
}

function mergeLogRows(
  prev: DesignScopeListItem[],
  updated: DesignScopeListItem[]
): DesignScopeListItem[] {
  const items = [...prev];
  updated.forEach((item) => {
    if (!item?.id) return;
    const foundIndex = items.findIndex((row) => row.id === item.id);
    if (foundIndex === -1) {
      items.push(item);
    } else {
      items[foundIndex] = item;
    }
  });
  return items;
}

/**
 * Design Scope list data source: GET /report-engine/logs/10.
 * Waits for a project token; otherwise keeps an empty list.
 * On failure, keeps the last successful rows and sets `loadFailed`
 * so the page does not treat the error as a genuine empty project.
 *
 * Also applies incremental create/update/delete from ProjectContext
 * `eventLogs` (same shape as Material / Submittal feature logs).
 */
export function useDesignScopeList(projectToken?: string | null) {
  const { eventLogs } = useContext(ProjectContext);
  const [items, setItems] = useState<DesignScopeListItem[]>([]);
  const [loading, setLoading] = useState(Boolean(projectToken));
  const [loadFailed, setLoadFailed] = useState(false);

  const initialFetchDone = useRef(false);
  const patchRequestId = useRef(0);
  const previousEventLogs = useRef(eventLogs);

  const reload = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent === true;

      if (!projectToken) {
        setItems([]);
        setLoadFailed(false);
        setLoading(false);
        initialFetchDone.current = false;
        return;
      }

      // Invalidate in-flight partial patches so they cannot overwrite a full reload.
      patchRequestId.current += 1;
      if (!silent) setLoading(true);
      try {
        const res = await getFeatureEntityLog({
          projectToken,
          featureType: DESIGN_SCOPE_FEATURE_TYPE
        });

        if (res.success && Array.isArray(res.data)) {
          setItems(res.data as DesignScopeListItem[]);
          setLoadFailed(false);
        } else {
          // Keep prior items; mark failure so the empty-project CTA is suppressed.
          setLoadFailed(true);
        }
      } catch {
        setLoadFailed(true);
      } finally {
        initialFetchDone.current = true;
        // Always clear: a silent refresh may have superseded one that showed the spinner.
        setLoading(false);
      }
    },
    [projectToken]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const getAndUpdateEntityList = useCallback(
    async (featureInstanceIds: string[]) => {
      if (!projectToken || !featureInstanceIds.length) return;

      if (featureInstanceIds.length >= MAX_NUMBER_OF_FETCH_ITEM) {
        await reload({ silent: true });
        return;
      }

      const requestId = ++patchRequestId.current;
      const res = await getFeatureEntityLog({
        projectToken,
        featureType: DESIGN_SCOPE_FEATURE_TYPE,
        feature_instance_ids: featureInstanceIds
      });

      if (requestId !== patchRequestId.current) return;
      if (!res?.success || !Array.isArray(res.data) || !res.data.length) return;

      setItems((prev) => mergeLogRows(prev, res.data as DesignScopeListItem[]));
    },
    [projectToken, reload]
  );

  const deleteEntitiesFromList = useCallback((ids: string[]) => {
    if (!ids.length) return;
    const remove = new Set(ids);
    setItems((prev) => prev.filter((row) => !remove.has(row.id)));
  }, []);

  useEffect(() => {
    if (!initialFetchDone.current) {
      previousEventLogs.current = eventLogs;
      return;
    }

    if (!eventLogs?.length || previousEventLogs.current === eventLogs) {
      previousEventLogs.current = eventLogs;
      return;
    }

    const featureLogs = eventLogs.filter(isDesignScopeFeatureLog);
    if (featureLogs.length) {
      const deletedIds = flattenFeatureInstanceIds(
        featureLogs.filter((log) => DELETE_CHANGE_TYPES.has(log.change_type))
      );
      if (deletedIds.length) {
        deleteEntitiesFromList(deletedIds);
      }

      const deletedSet = new Set(deletedIds);
      const upsertIds = flattenFeatureInstanceIds(
        featureLogs.filter((log) => !DELETE_CHANGE_TYPES.has(log.change_type))
      ).filter((id) => !deletedSet.has(id));

      if (upsertIds.length) {
        getAndUpdateEntityList(upsertIds);
      }
    }

    previousEventLogs.current = eventLogs;
  }, [deleteEntitiesFromList, eventLogs, getAndUpdateEntityList]);

  return {
    items,
    setItems,
    loading,
    loadFailed,
    reload,
    deleteEntitiesFromList
  };
}
